import { buildApiUrl } from '@/config/api'

export interface BlogPost {
  id: number
  title: string
  excerpt: string
  content: string
  category: string
  author: string
  featured: boolean
  status: 'draft' | 'published' | 'archived'
  created_at: string
  updated_at: string
}

export interface CreateBlogPostData {
  title: string
  excerpt: string
  content: string
  category: string
  author: string
  featured?: boolean
  status?: 'draft' | 'published' | 'archived'
}

export interface UpdateBlogPostData extends Partial<CreateBlogPostData> {
  id: number
}

class BlogService {
  private baseUrl = 'blog'

  async getBlogPosts(): Promise<BlogPost[]> {
    try {
      const response = await fetch(buildApiUrl(this.baseUrl), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      return data.posts || []
    } catch (error) {
      console.error('Error fetching blog posts:', error)
      throw error
    }
  }

  async getBlogPost(id: number): Promise<BlogPost> {
    try {
      const response = await fetch(buildApiUrl(`${this.baseUrl}/${id}`), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      return data.post
    } catch (error) {
      console.error('Error fetching blog post:', error)
      throw error
    }
  }

  async createBlogPost(postData: CreateBlogPostData, token: string): Promise<{ post_id: number }> {
    try {
      const response = await fetch(buildApiUrl(this.baseUrl), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token,
        },
        body: JSON.stringify(postData),
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      return data
    } catch (error) {
      console.error('Error creating blog post:', error)
      throw error
    }
  }

  async updateBlogPost(postData: UpdateBlogPostData, token: string): Promise<void> {
    try {
      const response = await fetch(buildApiUrl(`${this.baseUrl}/${postData.id}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token,
        },
        body: JSON.stringify(postData),
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
    } catch (error) {
      console.error('Error updating blog post:', error)
      throw error
    }
  }

  async deleteBlogPost(id: number, token: string): Promise<void> {
    try {
      const response = await fetch(buildApiUrl(`${this.baseUrl}/${id}`), {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token,
        },
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
    } catch (error) {
      console.error('Error deleting blog post:', error)
      throw error
    }
  }
}

export const blogService = new BlogService()
